/**
 * Training im Kalender (F-34 E1): Einheiten als Termine eintragen, eine
 * Einfuegung rueckgaengig machen, nach einer Planaenderung aktualisieren und
 * auf Wunsch rollierend weiterfuehren. Die Regeln stehen in
 * `src/domain/training/kalender.ts`.
 */
import { db } from '@/db/schema';
import { createEvent, getOccurrences } from '@/db/repositories/events';
import { inDenPapierkorb } from '@/db/repositories/papierkorb';
import {
  planen, STANDARD_FENSTER, STANDARD_UHRZEIT, veraltet, vorschlaege, zeitNach,
  type Belegt, type Entscheidung, type KalenderVorschlag, type PlanZeile,
} from '@/domain/training/kalender';
import type { KalenderEinstellungen, PlanEinheit, TrainingsPlan, Uebung } from '@/domain/training/typen';
import type { CalendarEvent, ID, LocalDate } from '@/domain/types';
import { addDays, atMinutes, today } from '@/lib/datetime';
import { newId } from '@/lib/utils/id';

export const STANDARD_KALENDER: KalenderEinstellungen = { uhrzeit: STANDARD_UHRZEIT, fenster: STANDARD_FENSTER, rollierend: false, wochen: 4 };

export async function trainingsTermine(): Promise<CalendarEvent[]> {
  return db.events.filter((e) => !!e.training).toArray();
}

/** Belegte Zeiten im Zeitraum: alle Vorkommen mit Uhrzeit (auch Training, ohne Ganztaegiges). */
export async function belegtZwischen(von: LocalDate, bis: LocalDate): Promise<Belegt[]> {
  const occ = await getOccurrences({ from: atMinutes(von, 0), to: atMinutes(addDays(bis, 1), 0) });
  return occ.filter((o) => !o.allDay).map((o) => ({ start: String(o.start), end: String(o.end), titel: o.title }));
}

interface Daten { plaene: TrainingsPlan[]; einheiten: PlanEinheit[]; bibliothek: ReadonlyMap<string, Uebung>; einstellungen: KalenderEinstellungen }

async function daten(): Promise<Daten> {
  const [plaene, einheiten, uebungen, e] = await Promise.all([
    db.trainingsplaene.toArray(), db.planEinheiten.toArray(), db.uebungen.toArray(), db.trainingsEinstellungen.get('standard'),
  ]);
  return { plaene, einheiten, bibliothek: new Map(uebungen.map((u) => [u.id, u])), einstellungen: { ...STANDARD_KALENDER, ...e?.kalender } };
}

/** Die Vorschau fuer einen Zeitraum. */
export async function vorschau(von: LocalDate, bis: LocalDate): Promise<PlanZeile[]> {
  const d = await daten();
  const [belegt, bestehend] = await Promise.all([belegtZwischen(von, bis), trainingsTermine()]);
  return planen(vorschlaege(d.plaene, d.einheiten, d.bibliothek, von, bis, d.einstellungen.uhrzeit), belegt, bestehend.map((t) => t.training!), d.einstellungen.fenster);
}

function notiz(v: KalenderVorschlag, bibliothek: ReadonlyMap<string, Uebung>): string {
  const teile = v.einheit.uebungen.map((pu) => `${pu.saetze.length}× ${bibliothek.get(pu.uebungId)?.name ?? 'Übung'}`);
  if (v.einheit.ausdauer?.distanzKm) teile.unshift(`${v.einheit.ausdauer.distanzKm} km`);
  return [`Training: ${v.einheit.name} (${v.dauerMin} min${v.geschaetzt ? ', geschätzt' : ''})`, ...teile].join('\n');
}

/** Traegt die Zeilen nach ihren Entscheidungen ein. Gibt die Kennung der Einfuegung zurueck. */
export async function eintragen(zeilen: { v: KalenderVorschlag; entscheidung: Entscheidung }[]): Promise<{ einfuegung: ID; anzahl: number }> {
  const d = await daten();
  const area = (await db.trainingsEinstellungen.get('standard'))?.sportBereichId;
  const areaId = area && (await db.areas.get(area)) ? area : null;
  const einfuegung = newId();
  let anzahl = 0;
  await db.transaction('rw', db.events, async () => {
    for (const { v, entscheidung } of zeilen) {
      const zeit = zeitNach(v, entscheidung);
      if (!zeit) continue;
      await createEvent({
        title: v.einheit.name, notes: notiz(v, d.bibliothek), allDay: false, start: zeit.start, end: zeit.end, tz: 'Europe/Berlin', areaId,
        training: { planEinheitId: v.einheit.id, planId: v.plan.id, datum: v.datum, einfuegung },
      });
      anzahl++;
    }
  });
  return { einfuegung, anzahl };
}

/** Ist die Einheit am Plantag schon absolviert? Dann bleibt der Termin stehen. */
async function absolviert(termine: CalendarEvent[]): Promise<Set<string>> {
  const tage = [...new Set(termine.map((t) => t.training!.datum))];
  const p = tage.length ? await db.trainingsprotokolle.where('datum').anyOf(tage).toArray() : [];
  return new Set(p.filter((x) => x.status === 'fertig' && x.planEinheitId).map((x) => `${x.planEinheitId}|${x.datum}`));
}

const offen = (t: CalendarEvent, heute: LocalDate, fertig: Set<string>) => t.training!.datum >= heute && !fertig.has(`${t.training!.planEinheitId}|${t.training!.datum}`);

export interface Einfuegung { id: ID; von: LocalDate; bis: LocalDate; anzahl: number; offen: number }

export async function einfuegungen(heute = today()): Promise<Einfuegung[]> {
  const termine = await trainingsTermine();
  const fertig = await absolviert(termine);
  const nach = new Map<ID, CalendarEvent[]>();
  for (const t of termine) nach.set(t.training!.einfuegung, [...(nach.get(t.training!.einfuegung) ?? []), t]);
  return [...nach].map(([id, ts]) => {
    const tage = ts.map((t) => t.training!.datum).sort();
    return { id, von: tage[0]!, bis: tage.at(-1)!, anzahl: ts.length, offen: ts.filter((t) => offen(t, heute, fertig)).length };
  }).sort((a, b) => b.von.localeCompare(a.von));
}

/** Rueckgaengig: entfernt die noch nicht absolvierten Termine einer Einfuegung ab heute. */
export async function einfuegungEntfernen(id: ID, heute = today()): Promise<number> {
  const termine = (await trainingsTermine()).filter((t) => t.training!.einfuegung === id);
  const fertig = await absolviert(termine);
  const weg = termine.filter((t) => offen(t, heute, fertig)).map((t) => t.id);
  if (!weg.length) return 0;
  // Als EIN Papierkorb-Eintrag (F-45).
  await inDenPapierkorb({ art: 'geloescht', tabelle: 'events', zeileIds: weg, titel: 'Eingetragene Trainings' }, () => db.events.bulkDelete(weg));
  return weg.length;
}

/** Kuenftige, noch offene Termine aus einer nicht mehr gueltigen Planversion. */
export async function veralteteTermine(heute = today()): Promise<CalendarEvent[]> {
  const [termine, plaene] = await Promise.all([trainingsTermine(), db.trainingsplaene.toArray()]);
  const kandidaten = veraltet(termine, plaene, heute);
  const fertig = await absolviert(kandidaten);
  return kandidaten.filter((t) => offen(t, heute, fertig));
}

/** Automatisch eintragen (ohne Vorschau): frei = eintragen, Konflikt = verschieben oder weglassen. */
async function automatisch(von: LocalDate, bis: LocalDate): Promise<number> {
  if (bis < von) return 0;
  const zeilen = (await vorschau(von, bis)).filter((z) => z.zustand !== 'schon' && z.entscheidung.art !== 'weglassen');
  return zeilen.length ? (await eintragen(zeilen)).anzahl : 0;
}

/** Nach einer Planaenderung: veraltete kuenftige Termine durch die gueltige Version ersetzen. */
export async function veralteteAktualisieren(heute = today()): Promise<number> {
  const alt = await veralteteTermine(heute);
  if (!alt.length) return 0;
  const tage = alt.map((t) => t.training!.datum).sort();
  await db.events.bulkDelete(alt.map((t) => t.id));
  return automatisch(tage[0]!, tage.at(-1)!);
}

/** Rollierend (Schalter, Standard aus): haelt die naechsten X Wochen gefuellt. */
export async function rollierendAuffuellen(heute = today()): Promise<number> {
  const e = (await daten()).einstellungen;
  if (!e.rollierend) return 0;
  return automatisch(heute, addDays(heute, e.wochen * 7 - 1));
}
